import React, { useState } from 'react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create mailto link with form data
      const subject = encodeURIComponent(formData.subject);
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      );
      const mailtoLink = `mailto:kiratmehta92@gmail.com?subject=${subject}&body=${body}`;
      
      // Open default email client
      window.location.href = mailtoLink;
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const contactInfo = [
    {
      icon: 'fas fa-envelope',
      title: 'Email',
      value: 'kiratmehta92@gmail.com',
      link: 'mailto:kiratmehta92@gmail.com'
    },
    {
      icon: 'fas fa-phone',
      title: 'Phone',
      value: '(226) 934-7837',
      link: 'tel:+12269347837'
    },
    {
      icon: 'fab fa-linkedin',
      title: 'LinkedIn',
      value: 'kiratmehta',
      link: 'https://www.linkedin.com/in/kiratmehta/'
    },
    {
      icon: 'fab fa-github',
      title: 'GitHub',
      value: 'SilentMic',
      link: 'https://github.com/SilentMic'
    },
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Location',
      value: 'Ontario, Canada',
      link: null
    }
  ];

  return (
    <section id="contact" className="py-20 bg-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 fade-in">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto fade-in">
            I'm always interested in new opportunities and exciting projects. 
            Let's connect and discuss how we can work together!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Contact Form */}
          <div className="slide-in-left">
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-slate-700/50">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <i className="fas fa-paper-plane text-primary mr-3"></i>
                Send Me a Message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="form-group">
                    <label htmlFor="name" className="block text-gray-300 font-medium mb-2 text-sm">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="block text-gray-300 font-medium mb-2 text-sm">
                      Your Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="block text-gray-300 font-medium mb-2 text-sm">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                    placeholder="What's this about?"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="block text-gray-300 font-medium mb-2 text-sm">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 resize-none"
                    placeholder="Tell me about your project or opportunity..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-[1.02] shadow-lg ${
                    isSubmitting
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary/90 pulse-btn hover:shadow-primary/50'
                  } text-white`}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Send Message
                    </>
                  )}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500/50 text-green-400 p-4 rounded-lg flex items-start">
                    <i className="fas fa-check-circle mr-3 mt-0.5 flex-shrink-0"></i>
                    <span>Your email client should have opened with the message. Please send it from there!</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-4 rounded-lg flex items-start">
                    <i className="fas fa-exclamation-circle mr-3 mt-0.5 flex-shrink-0"></i>
                    <span>Something went wrong. Please try again or contact me directly.</span>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className="slide-in-right">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <i className="fas fa-address-card text-primary mr-3"></i>
                  Contact Information
                </h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-base">
                  Feel free to reach out through any of these channels. I'm always happy to discuss 
                  new opportunities, answer questions, or just have a chat about technology and development.
                </p>
              </div>

              {/* Contact Items */}
              <div className="space-y-4">
                {contactInfo.map((item, index) => (
                  <div key={index} className="contact-item flex items-center p-4 bg-slate-800/30 backdrop-blur-sm rounded-lg border border-slate-700/50 hover:bg-slate-800/50 hover:border-primary/50 transition-all duration-300">
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className={`${item.icon} text-primary text-xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                      {item.link ? (
                        <a
                          href={item.link}
                          target={item.link.startsWith('http') ? '_blank' : '_self'}
                          rel={item.link.startsWith('http') ? 'noopener noreferrer' : ''}
                          className="text-gray-400 hover:text-primary transition-colors duration-300 text-sm break-words"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="bg-slate-800/30 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50">
                <h4 className="text-white font-semibold mb-4 flex items-center">
                  <i className="fas fa-share-alt text-primary mr-2"></i>
                  Connect With Me
                </h4>
                <div className="flex space-x-4">
                  <a
                    href="https://github.com/SilentMic"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-primary transition-all duration-300"
                    aria-label="GitHub"
                  >
                    <i className="fab fa-github text-xl"></i>
                  </a>
                  <a
                    href="https://www.linkedin.com/in/kiratmehta/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-primary transition-all duration-300"
                    aria-label="LinkedIn"
                  >
                    <i className="fab fa-linkedin text-xl"></i>
                  </a>
                  <a
                    href="mailto:kiratmehta92@gmail.com"
                    className="social-link w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-primary transition-all duration-300"
                    aria-label="Email"
                  >
                    <i className="fas fa-envelope text-xl"></i>
                  </a>
                </div>
              </div>

              {/* Availability */}
              <div className="bg-slate-800/30 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50">
                <h4 className="text-white font-semibold mb-4 flex items-center">
                  <i className="fas fa-clock text-primary mr-2"></i>
                  Availability
                </h4>
                <div className="space-y-2.5 text-gray-300 text-sm">
                  <p className="flex items-start"><i className="fas fa-check-circle text-green-400 mr-3 mt-0.5 flex-shrink-0"></i><span>Available for new projects</span></p>
                  <p className="flex items-start"><i className="fas fa-check-circle text-green-400 mr-3 mt-0.5 flex-shrink-0"></i><span>Open to remote opportunities</span></p>
                  <p className="flex items-start"><i className="fas fa-check-circle text-green-400 mr-3 mt-0.5 flex-shrink-0"></i><span>Freelance & contract work</span></p>
                  <p className="flex items-start"><i className="fas fa-clock text-yellow-400 mr-3 mt-0.5 flex-shrink-0"></i><span>Response time: 24-48 hours</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;